import React from 'react'
import cx from 'classnames'
import PopperJS from 'popper.js'
import DynamicContent from '../dynamic-content'

class Tooltip extends React.PureComponent {
  static defaultProps = {
    el: 'div',
    className: '',
    offset: '0,8', // x,y
    overlay: '',
    onClose: () => {},
    onOpen: () => {},
    padding: 10,
    placement: 'top',
    zIndex: 10,
    preventDefault: false,
    stopPropagation: false,
    track: null,
    trackAs: null
  }

  state = {
    isActive: false,
    isOpen: false
  }

  // Only install popper when we need it. Avoids a lot of unnecessary
  // layout thrashing
  installed = false

  install() {
    // Popper returns when it finds the first element with computed style containing `auto` or `scroll`.
    // Send a CSS selector as a prop to update position during scroll of another element.
    if (this.props.scrollParent) {
      this.scrollElement = document.querySelector(this.props.scrollParent)
    }

    this.popperInstance = new PopperJS(
      this.content,
      this.popper,
      this.getPopperOptions()
    )

    // ensure paint has completed before calculating element positions
    // initial tooltip placement is otherwise incorrect
    setTimeout(() => {
      window.requestAnimationFrame(this.updatePosition)
    }, 0)

    this.closeHandler = this.autoHide.bind(this)
    window.addEventListener('click', this.closeHandler, true)

    if (this.scrollElement) {
      this.scrollHandler = this.updatePosition.bind(this)
      this.scrollElement.addEventListener('scroll', this.scrollHandler)
    }

    this.installed = true
  }

  componentWillUpdate(prevProps, prevState) {
    if (!this.installed && prevState.isOpen) {
      this.install()
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.isOpen !== this.state.isOpen) {
      this.setState({ isActive: true })

      this.activeTimeout = setTimeout(() => {
        window.requestAnimationFrame(() => {
          this.setState({ isActive: false })
        })
      }, 250)
    }
  }

  componentWillReceiveProps(props, nextProps) {
    if (props.overlay !== nextProps.overlay) {
      this.updatePosition()
    }
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.closeHandler, true)

    if (this.activeTimeout) {
      clearTimeout(this.activeTimeout)
    }

    if (this.scrollElement && this.scrollHandler) {
      this.scrollElement.removeEventListener('scroll', this.scrollHandler)
    }

    if (this.popperInstance) {
      this.popperInstance.destroy()
    }
  }

  autoHide(event) {
    if (this.content.contains(event.target)) {
      return null
    }

    this.close()
  }

  getPopperOptions() {
    return {
      placement: this.props.placement,
      modifiers: {
        arrow: { element: this.arrow },
        applyStyle: { enabled: false },
        offset: { offset: this.props.offset },
        preventOverflow: {
          boundariesElement: this.scrollElement || 'scrollParent',
          padding: this.props.padding, // relative to parent boundary
          priority: ['top', 'bottom', 'right', 'left']
        }
      },
      onCreate: data => this.setState({ data }),
      onUpdate: data => this.setState({ data })
    }
  }

  getPopperStyle(data) {
    if (!data) {
      return {}
    }

    const left = Math.round(data.offsets.popper.left)
    const top = Math.round(data.offsets.popper.top)

    return {
      left,
      position: data.offsets.popper.position,
      top,
      zIndex: this.props.zIndex
    }
  }

  getArrowStyle(data) {
    if (!data) {
      return {}
    }

    const buttonMarginRight =
      parseInt(getComputedStyle(this.button)['margin-right'], 10) / 2
    const left = Math.round(data.offsets.arrow.left) - buttonMarginRight
    const top = Math.round(data.offsets.arrow.top)
    const style = {
      position: 'absolute'
    }

    switch (this.props.placement) {
      case 'top':
        return { ...style, left }
      case 'left':
        return { ...style, right: 0, top }
      default:
        return { ...style, left, top }
    }
  }

  open = () => {
    this.setState({ isOpen: true })

    setTimeout(() => {
      this.props.onOpen(this.popper)
    }, 0)
  }

  close = () => {
    this.setState({ isOpen: false })
    this.props.onClose(this.popper)
  }

  toggle = event => {
    this.state.isOpen ? this.close() : this.open()
    this.props.preventDefault && event.preventDefault()
    this.props.stopPropagation && event.stopPropagation()
  }

  updatePosition = () => {
    if (this.popperInstance) {
      this.popperInstance.scheduleUpdate()
    }
  }

  render() {
    const { className, el: Element, overlay, track, trackAs } = this.props
    const { data, isActive, isOpen } = this.state

    const wrapperClassName = cx('tooltip', className, {
      '-open': isOpen,
      '-active': isActive
    })

    const trackAsClass = trackAs ? `track-as-${trackAs}` : null
    const buttonClassName = cx('tooltip__button', trackAsClass)

    return (
      <Element ref={el => (this.content = el)} className={wrapperClassName}>
        <button
          title="Tooltip"
          type="button"
          className={buttonClassName}
          ref={el => {
            this.button = el
          }}
          onClick={this.toggle}
          data-track={track}
        >
          {this.props.children}
        </button>
        <div
          ref={el => (this.popper = el)}
          className={`tooltip__tip -${
            data ? data.placement : this.props.placement
          }`}
          style={this.getPopperStyle(data)}
        >
          <DynamicContent className="tooltip__tip__inner">
            {overlay}
          </DynamicContent>

          <div
            ref={el => (this.arrow = el)}
            className="tooltip__tip__arrow"
            style={this.getArrowStyle(data)}
          />
        </div>
      </Element>
    )
  }
}

export default Tooltip



// WEBPACK FOOTER //
// /home/runner/work/web-interactive/web-interactive/lib/components/tooltip/index.js